import { useFormContext } from 'react-hook-form';
import { useEffect, useRef, useState } from 'react';
import {
  X,
  Plus,
  Building2,
  Home,
  Video,
  MapPin,
  Search,
  Check,
} from 'lucide-react';
import type { ComponentType, SVGProps } from 'react';
import { Icon } from '@scripsteam/scrips-signal-ds';
import { AnchoredPopover, AnchoredPopoverContent, AnchoredPopoverTrigger } from '@scripsteam/scrips-signal-ds';
import { cn } from '@/lib/utils';
import { useDebounce } from '@/shared/hooks/use-debounce';
import { useAppointmentProfiles } from '@/services/scheduling/use-appointment-profiles';
import { useServiceFees, type ServiceFee } from '@/services/billing/use-service-fees';
// Cross-domain import (scheduling → billing) — an established, shipped
// pattern in this module (see edit-insurance-view.tsx + create-patient-view.tsx).
// The org billing settings carry `followUpValidity`, needed for the
// free-of-charge follow-up banner (F-SCH-137).
import { useBillingSettings } from '@/billing/services/use-billing-settings';
import { isFreeFollowUpEligible } from '@/scheduling/lib/free-followup';
import { profileColorVars } from '@/scheduling/lib/profile-color-vars';
import {
  usePreBookAppointmentProfile,
  pickDefaultReminder,
  pickAttachedBillingProfile,
  reminderRules,
  formatBillingProfile,
  billingProfileList,
  type PreBookAppointmentProfileResponse,
} from '@/services/scheduling/use-prebook-appointment-profile';
import { usePreBookSelectedSlot } from '@/services/scheduling/use-prebook-selected-slot';
import type { AppointmentFormValues } from '@/types/appointment-form';
import type { CreateAppointmentProfileRequest } from '@/types/scheduling-api';
// PROD-2743 design sheet §3 (icons): DS names ON only (?ef=frameV2); OFF keeps today's lucide icon, unchanged.

interface AppointmentProfileSectionProps {
  organizationId: string;
  practiceId: string;
  /**
   * When set (RESCHEDULE mode), the picker stays enabled but only lists
   * profiles whose appointment-type matches this code — the user may swap to a
   * different same-type profile/slot but cannot change the appointment's type.
   * Mirrors Flutter `appointment_profile_widget.dart` reschedule constraint.
   * Null/undefined (create/edit) leaves the list unconstrained by type.
   *
   * NOTE: intentionally a UI-only constraint (same posture as Flutter and the
   * create/edit picker) — it narrows what the dropdown surfaces, not a backend
   * guard. Reschedule save validation is unchanged from create/edit.
   */
  constrainToAppointmentTypeCode?: string | null;
}

// The profile colour string arrives from data (the profile record), so it is a
// runtime value: it is handed to the element as the `--pa-profile-color` custom
// property and consumed by token-bearing classes. Unparseable input falls back
// to the DS hint grey.
const PROFILE_COLOR_FALLBACK = 'var(--color-text-hint)';

/**
 * AppointmentProfileSection — picks the profile that drives slot duration,
 * reasonCode, room, billing, and appointmentType. Renders only after a
 * provider is selected (mirrors Flutter `appointment_profile_widget.dart`).
 */
export function AppointmentProfileSection({
  organizationId,
  practiceId,
  constrainToAppointmentTypeCode,
}: AppointmentProfileSectionProps) {
  const { watch, setValue } = useFormContext<AppointmentFormValues>();
  const providerId = watch('providerId');
  const selectedDate = watch('selectedDate');
  const selectedProfileId = watch('appointmentProfileId');
  const selectedProfileName = watch('appointmentProfileName');
  const selectedColor = watch('appointmentProfileColor');
  const selectedDuration = watch('minutesDuration');
  const selectedReason = watch('reasonCode');
  const reasonOptions = watch('reasonCodeOptions');
  const location = watch('location');
  const billingProfileName = watch('billingProfileName');
  const billingProfileId = watch('billingProfileId');
  const billingProfilesAll = watch('billingProfilesList');

  // Local state for the three picker popovers — controlled so the
  // search inputs reset between opens and we can close on selection.
  const [reasonOpen, setReasonOpen] = useState(false);
  const [reasonSearch, setReasonSearch] = useState('');
  const [billingOpen, setBillingOpen] = useState(false);
  const [billingSearch, setBillingSearch] = useState('');
  const [procedureOpen, setProcedureOpen] = useState(false);
  // Edit mode — expands the alternative-profile grid below the
  // selected card so the user can switch profiles without first
  // clearing (Flutter `appointment_profile_widget.dart` "edit" flow).
  const [profileEditOpen, setProfileEditOpen] = useState(false);

  const { data, isLoading } = useAppointmentProfiles(
    providerId ? { organizationId, practiceId, providerId } : null,
  );
  const allProfiles = Array.isArray(data) ? data : [];
  // Flutter parity: only profiles whose `location.name` matches the
  // currently picked location are shown (Office picks → Office profiles
  // only, Virtual → Virtual, Home → Home). Compare case-insensitively
  // because some endpoints ship the name capitalised, others not.
  const selectedLocationName = (location?.name ?? '').toLowerCase();
  // PROD-920 — when entering this panel from the FU Requests "Create
  // Follow Up" kebab, `referenceAppointmentId` is pre-set on the form
  // (frame-scheduling.tsx#handleFuCreateFollowUp). That's the
  // unambiguous signal that we're in the follow-up flow — restrict the
  // profile picker to FOLLOWUP-type profiles only. Mirrors Flutter
  // `calendar_screen.dart:182` (`selectedAppointmentType:
  // appointmentTypeFollowUp`) → downstream Flutter widget filters
  // profiles by that locked type.
  const referenceAppointmentId = watch('referenceAppointmentId');
  const isFollowUpFlow = !!referenceAppointmentId;
  const profiles = allProfiles.filter((p) => {
    if (
      selectedLocationName &&
      (p.location?.name ?? '').toLowerCase() !== selectedLocationName
    ) {
      return false;
    }
    if (isFollowUpFlow) {
      // PascalCase tolerance — some appointment-profile endpoints ship
      // the appointmentType keys capitalised (same as `selectProfile`
      // below handles).
      const at = p.appointmentType as
        | (typeof p.appointmentType & { Code?: string })
        | undefined;
      const code = at?.code ?? at?.Code;
      if (code !== 'FOLLOWUP') return false;
    }
    if (constrainToAppointmentTypeCode) {
      // RESCHEDULE mode — restrict to the same appointment-type as the
      // appointment being moved (same PascalCase tolerance as above).
      const at = p.appointmentType as
        | (typeof p.appointmentType & { Code?: string })
        | undefined;
      const code = at?.code ?? at?.Code;
      if (code !== constrainToAppointmentTypeCode) return false;
    }
    return true;
  });

  // Mutation that fires the PreBook chain on profile pick — auto-populates
  // reminderProfile (required for save) + billingProfile.
  const preBook = usePreBookAppointmentProfile();
  // Slot-mode variant — Flutter `calendar_screen.dart:347-353` dispatches
  // `PreBookSelectedSlotEvent` instead when a slot is already known.
  // Both endpoints return identical response shape but
  // `PreBookSelectedSlot` populates `providerReasonForVisits` more
  // reliably for the slot case.
  const preBookSlot = usePreBookSelectedSlot();
  const slotStart = watch('slotStart');
  // F-SCH-137 inputs — selected profile type, the previous-appointment date
  // (follow-up flow), and the org's follow-up validity window.
  const appointmentType = watch('appointmentType');
  const previousAppointmentDate = watch('previousAppointmentDate');
  const { data: billingSettings } = useBillingSettings(organizationId);

  /**
   * Quick-create hydration (PROD-829): when the panel mounts with a
   * profile already selected (via `initialValues` from
   * `frame-scheduling.tsx#handleQuickCreate`), the user didn't click
   * `selectProfile()` so the PreBook chain never ran. The reminder /
   * billing / reasonOptions fields stay empty, the reason field
   * shows no options to pick from, and Save validation can't fire.
   *
   * This effect detects "profile is selected but reasonCodeOptions is
   * empty" — a clear hydrated-without-prebook signature — and fires
   * the same PreBook mutation the section's `selectProfile()` runs.
   * `hydratedRef` guards against re-firing if the user navigates away
   * and back.
   */
  // Pre-filled profile carried in by quick-create (PROD-829) — `pickedProfile`
  // holds the FULL `CreateAppointmentProfileRequest` (including the embedded
  // `billingProfile` object). This is the canonical source for billing
  // lookup in the hydration path below — reading from `allProfiles.find()`
  // hits a race condition where the GET `/api/Doctor/AppointmentProfileList`
  // hasn't resolved yet when the PreBook response arrives, so the closure
  // sees an empty list. `form.pickedProfile` is set synchronously by
  // `handleQuickCreate` (frame-scheduling.tsx) so it's available from the
  // first effect run.
  const pickedProfile = watch('pickedProfile') as
    | (CreateAppointmentProfileRequest & {
        billingProfile?: { id?: string; name?: string; totalCost?: number; totalAmount?: number } | null;
      })
    | null
    | undefined;

  const hydratedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!selectedProfileId || !providerId || !selectedDate) return;
    if (hydratedRef.current === selectedProfileId) return;
    // Skip when the section's own `selectProfile()` already populated.
    if (reasonOptions.length > 0 || (billingProfilesAll?.length ?? 0) > 0) return;
    hydratedRef.current = selectedProfileId;

    const handlePreBookResponse = (response: PreBookAppointmentProfileResponse) => {
      const reminder = pickDefaultReminder(response.reminderList);
      if (reminder?.id) {
        setValue('reminderProfileId', reminder.id, { shouldValidate: true });
        setValue('reminderProfileName', reminder.profileName ?? null);
        setValue('reminderProfileRules', reminderRules(reminder));
        setValue('reminderProfile', reminder);
      }
      const billingList = billingProfileList(response.billingProfiles);
      setValue('billingProfilesList', billingList);
      // Source priority for the picked profile (Flutter parity):
      //   1. `pickedProfile` from form — set by quick-create's
      //      `buildProfileFormValues(profile)`. Carries the FULL profile
      //      including the embedded `billingProfile` object. Available
      //      from the first render.
      //   2. `response.appointmentProfileResponseList.createAppointmentProfileRequest`
      //      — the FRESH profile list bundled with the PreBook response
      //      (Flutter `create_appointment_widget.dart:2275-2280` reads
      //      from here). Always up-to-date.
      //   3. `allProfiles.find()` — fallback to the GET list. Races the
      //      PreBook response on cold cache so it's last resort.
      // `appointmentProfileResponseList` is typed as `unknown` on the
      // shared response type (different endpoints ship variant shapes).
      // Cast through the known runtime shape — Flutter
      // `create_appointment_widget.dart:2275-2280` reads this same path.
      //
      // Runtime guards (Samer review §2 — defend against malformed
      // responses that would otherwise silently fall through to
      // `allProfiles.find()` and re-surface Bug C): require
      // `appointmentProfileResponseList` to be a non-null object AND
      // `createAppointmentProfileRequest` to be an array before
      // attempting the lookup.
      const aplRaw = response.appointmentProfileResponseList;
      const apl =
        aplRaw && typeof aplRaw === 'object' && !Array.isArray(aplRaw)
          ? (aplRaw as { createAppointmentProfileRequest?: unknown })
          : undefined;
      const aplList = Array.isArray(apl?.createAppointmentProfileRequest)
        ? (apl.createAppointmentProfileRequest as CreateAppointmentProfileRequest[])
        : undefined;
      const responseFreshProfile = aplList?.find(
        (p) => p?.id === selectedProfileId,
      );
      const fullProfile =
        pickedProfile ?? responseFreshProfile ?? allProfiles.find((p) => p.id === selectedProfileId);
      if (fullProfile) {
        const billing = pickAttachedBillingProfile(fullProfile, billingList);
        if (billing?.id) {
          setValue('billingProfileId', billing.id);
          setValue('billingProfileName', formatBillingProfile(billing));
        }
      }
      // Dropdown options come from `providerReasonForVisits`. Merge in
      // the picked profile's own reasonCode so the currently-selected
      // value (which `buildProfileFormValues` seeds from
      // `profile.reasonCode`) always has a matching dropdown entry —
      // otherwise the picker opens with no item highlighted as
      // selected, even though the field DISPLAYS the profile's reason.
      // Dedupe by `code` (preferred) then by `display` so a backend
      // that ships the same RFV in both lists doesn't duplicate.
      const remoteOptions = (response.providerReasonForVisits ?? []).map((r) => ({
        code: r.code,
        display: r.display,
        definition: r.definition,
      }));
      const profileReason = fullProfile?.reasonCode;
      const profileReasonCanonical =
        profileReason?.code && profileReason?.display
          ? {
              code: profileReason.code,
              display: profileReason.display,
              definition: profileReason.definition ?? '',
            }
          : null;
      const merged = profileReasonCanonical
        ? [
            profileReasonCanonical,
            ...remoteOptions.filter(
              (r) =>
                r.code !== profileReasonCanonical.code &&
                (r.display ?? '').toLowerCase() !==
                  (profileReasonCanonical.display ?? '').toLowerCase(),
            ),
          ]
        : remoteOptions;
      if (merged.length > 0) {
        setValue('reasonCodeOptions', merged);
      }
    };

    // Route by slot presence — Flutter parity:
    //   slot present → PreBookSelectedSlot (Case A/B, calendar_screen.dart:347)
    //   slot null    → PreBookSelectedAppointmentProfile (Case C, :386)
    //
    // Use `mutateAsync().then()` instead of `mutate(opts, callbacks)`:
    // RQ v5 per-call callbacks are skipped when the calling component
    // unmounts mid-flight (StrictMode dev double-mount, HMR module
    // replacement, etc.). The promise from mutateAsync resolves
    // regardless, so the hydration always reaches its setValue calls
    // even when the dev tooling cycles the tree.
    if (slotStart) {
      preBookSlot
        .mutateAsync({
          organizationId,
          practiceId,
          providerId,
          appointmentProfileId: selectedProfileId,
        })
        .then(handlePreBookResponse)
        .catch(() => {
          // Global error toast in api-client surfaces the failure; just
          // unblock the hydratedRef so a retry on next mount can refire.
          hydratedRef.current = null;
        });
    } else {
      preBook
        .mutateAsync({
          organizationId,
          practiceId,
          providerId,
          appointmentProfileId: selectedProfileId,
          date: selectedDate,
        })
        .then(handlePreBookResponse)
        .catch(() => {
          hydratedRef.current = null;
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProfileId, providerId, selectedDate, slotStart]);

  if (!providerId) return null;

  // Show the selected card whenever the form has a selection, even if the
  // GET list is empty/stale (e.g. profile was auto-picked from the inline
  // ProviderSlots response without a separate AppointmentProfileList round-trip).
  const isSelected = !!selectedProfileId;
  // Resolve from the unfiltered list — the selected card needs to render
  // with its real color even if the user changes location to one that
  // excludes the picked profile.
  const fromList = allProfiles.find((p) => p.id === selectedProfileId);
  const dotColor = selectedColor ?? fromList?.color;

  // F-SCH-137: "Eligible for a free of charge follow-up." banner — Flutter
  // `appointment_profile_widget.dart:633-680`. Eligibility (follow-up profile +
  // within the org's followUpValidity window of the previous appointment) is a
  // pure function so the Dart `.inDays` parity is unit-tested. Slot start must
  // exist (Flutter gates on `start != null`), so the banner only appears once a
  // slot is picked.
  const showFreeFollowUpBanner = isFreeFollowUpEligible({
    appointmentTypeCode: appointmentType?.code,
    previousAppointmentDate,
    slotStart,
    followUpValidityDays: billingSettings?.followUpValidity ?? 0,
  });

  const selectProfile = (profile: CreateAppointmentProfileRequest) => {
    setValue('appointmentProfileId', profile.id ?? null, { shouldValidate: true });
    setValue('appointmentProfileName', profile.profileName);
    setValue('appointmentProfileColor', profile.color ?? null);
    // Tolerate PascalCase keys (`Code`/`Display`/`Definition`) — some
    // appointment-profile endpoints ship them that way. Drop the value
    // entirely when neither shape yields a code, otherwise the schema's
    // `code: z.string()` rejects an `undefined` and Save stays disabled
    // with no obvious fix.
    const at = profile.appointmentType as
      | (typeof profile.appointmentType & {
          Code?: string;
          Display?: string;
          Definition?: string;
        })
      | undefined;
    const atCode = at?.code ?? at?.Code;
    const atDisplay = at?.display ?? at?.Display;
    setValue(
      'appointmentType',
      atCode && atDisplay
        ? {
            code: atCode,
            display: atDisplay,
            definition: at?.definition ?? at?.Definition ?? '',
          }
        : null,
      { shouldValidate: true },
    );
    // Same PascalCase tolerance as `appointmentType` above — some
    // profile-list endpoints ship `ReasonCode` capitalised. Coerce to
    // null when neither shape yields a code/display so the schema's
    // `reasonCode.code required` rule (Flutter saveAppointmentButton
    // Status:1336) doesn't fire on a half-shaped value.
    const rc = profile.reasonCode as
      | (typeof profile.reasonCode & {
          Code?: string;
          Display?: string;
          Definition?: string;
        })
      | undefined;
    const rcCode = rc?.code ?? rc?.Code;
    const rcDisplay = rc?.display ?? rc?.Display;
    const profileReasonCode =
      rcCode && rcDisplay
        ? {
            code: rcCode,
            display: rcDisplay,
            definition: rc?.definition ?? rc?.Definition ?? '',
          }
        : null;

    // Reason-code carve-out for the FU Create Follow Up flow — Flutter
    // parity, `create_appointment_widget.dart:1089-1102`:
    //
    //   if (profile.appointmentType.code == "FOLLOWUP"
    //       && appointment.previousAppointmentRFV != null) {
    //     if (appointment.reasonCode != null) {
    //       // PRESERVE existing reasonCode; push it onto the profile.
    //       appointment.appointmentProfile.reasonCode = appointment.reasonCode;
    //     } else {
    //       appointment.reasonCode = profile.reasonCode;
    //     }
    //   } else {
    //     appointment.reasonCode = profile.reasonCode;  // normal flow
    //   }
    //
    // React signals "FU flow" via referenceAppointmentId non-null
    // (same convention the profile-filter above uses). When in FU flow
    // AND form already has a reasonCode (seeded by
    // `handleFuCreateFollowUp` from `row.reasonCode`), preserve it
    // and patch the picked profile's reasonCode so the wire
    // appointmentProfile.reasonCode also carries the FU request's
    // RFV (consistent top-level + nested values).
    const existingReasonCode = watch('reasonCode');
    const inFollowUpFlow = !!referenceAppointmentId;
    const preserveFuRfv =
      inFollowUpFlow && atCode === 'FOLLOWUP' && existingReasonCode != null;

    if (preserveFuRfv) {
      // Don't touch form.reasonCode — keeps the FU request's RFV.
      // Patch the picked profile so the wire's
      // appointmentProfile.reasonCode also carries it.
      setValue('pickedProfile', {
        ...profile,
        reasonCode: existingReasonCode,
      });
    } else {
      setValue('pickedProfile', profile);
      setValue(
        'reasonCode',
        profileReasonCode,
        // shouldValidate: true so the schema re-runs against the new
        // reasonCode. Earlier setValue calls in this function pass
        // shouldValidate, which fired validation while reasonCode was
        // still empty — leaving a stale "required" error.
        { shouldValidate: true },
      );
    }
    setValue('minutesDuration', profile.minutesDuration ?? null);
    setValue('slotId', null);
    setValue('slotStart', null);
    setValue('slotEnd', null);

    // Clear billing context before the new PreBook fires — switching to
    // a profile WITHOUT an attached billing profile must drop the
    // previous profile's billing, not silently inherit it. The PreBook
    // `.then()` below restores billing for the new profile if attached.
    setValue('billingProfileId', null);
    setValue('billingProfileName', null);
    setValue('billingProfilesList', []);

    if (profile.id && providerId && selectedDate) {
      // Use mutateAsync().then() — RQ v5 per-call onSuccess can skip
      // when StrictMode/HMR cycles the tree mid-flight. mutateAsync's
      // promise resolves regardless.
      preBook
        .mutateAsync({
          organizationId,
          practiceId,
          providerId,
          appointmentProfileId: profile.id,
          date: selectedDate,
        })
        .then((response) => {
          const reminder = pickDefaultReminder(response.reminderList);
          if (reminder?.id) {
            setValue('reminderProfileId', reminder.id, { shouldValidate: true });
            setValue('reminderProfileName', reminder.profileName ?? null);
            setValue('reminderProfileRules', reminderRules(reminder));
            setValue('reminderProfile', reminder);
          }
          const billingList = billingProfileList(response.billingProfiles);
          setValue('billingProfilesList', billingList);
          // Flutter sources the billing profile from the embedded
          // `appointmentProfile.billingProfile` (create_appointment_widget
          // .dart:2258). `pickAttachedBillingProfile` reads the embedded
          // object first, then falls back to a list lookup by
          // `billingProfileId` for endpoints that only ship the id.
          const billing = pickAttachedBillingProfile(profile, billingList);
          if (billing?.id) {
            setValue('billingProfileId', billing.id);
            setValue('billingProfileName', formatBillingProfile(billing));
          }
          // Merge the picked profile's reasonCode into the dropdown
          // options so the currently-selected RFV ("surgery" etc.)
          // shows as highlighted when the picker opens. Same dedupe
          // strategy as the hydration effect above.
          const remoteOptions = (response.providerReasonForVisits ?? []).map((r) => ({
            code: r.code,
            display: r.display,
            definition: r.definition,
          }));
          const profileReasonCanonical =
            profileReasonCode && profileReasonCode.code && profileReasonCode.display
              ? {
                  code: profileReasonCode.code,
                  display: profileReasonCode.display,
                  definition: profileReasonCode.definition ?? '',
                }
              : null;
          const merged = profileReasonCanonical
            ? [
                profileReasonCanonical,
                ...remoteOptions.filter(
                  (r) =>
                    r.code !== profileReasonCanonical.code &&
                    (r.display ?? '').toLowerCase() !==
                      (profileReasonCanonical.display ?? '').toLowerCase(),
                ),
              ]
            : remoteOptions;
          if (merged.length > 0) {
            setValue('reasonCodeOptions', merged);
          }
          // Auto-open the reason picker so the user's next step is clear (Practice's flow).
          setReasonOpen(true);
        })
        .catch(() => {
          // Global error toast in api-client surfaces failure.
        });
    } else {
      // No PreBook: open the reason picker straight away.
      setReasonOpen(true);
    }
  };

  const clearProfile = () => {
    setValue('appointmentProfileId', null, { shouldValidate: true });
    setValue('appointmentProfileName', null);
    setValue('appointmentProfileColor', null);
    setValue('appointmentType', null, { shouldValidate: true });
    setValue('reasonCode', null);
    setValue('reasonCodeOptions', []);
    setValue('minutesDuration', null);
    setValue('pickedProfile', null);
    setValue('reminderProfileId', null, { shouldValidate: true });
    setValue('reminderProfileName', null);
    setValue('reminderProfileRules', []);
    setValue('billingProfileId', null);
    setValue('billingProfileName', null);
    setValue('slotId', null);
    setValue('slotStart', null);
    setValue('slotEnd', null);
  };

  return (
    <div className="booking-section" data-slot="booking-section">
      <p className="booking-section-label">
        Appointment profile
      </p>

      {isSelected ? (
        <>
        <div className="booking-profile-card">
          {/* Header row (the design's `.profileTop`): colour dot + name + duration · location + edit + clear */}
          <div className="booking-profile-top">
            <span
              aria-hidden
              className="booking-dot bg-[var(--pa-profile-color)]"
              style={profileColorVars(parseColor(dotColor))}
            />
            <div className="flex-1 min-w-0">
              <p className="booking-card-title">{selectedProfileName}</p>
              <p className="booking-card-meta booking-profile-meta">
                <Icon name="appt-follow-up" size={12} aria-hidden />
                <span>
                  {selectedDuration ?? '–'} minutes
                  {location?.name ? ` · ${PROFILE_LOCATION_LABEL[location.name] ?? location.name}` : ''}
                </span>
              </p>
            </div>
            <button
              type="button"
              onClick={() => setProfileEditOpen((v) => !v)}
              className={cn('booking-row-icon-button', profileEditOpen && 'booking-row-icon-button-on')}
              aria-label="Edit profile"
              aria-pressed={profileEditOpen}
            >
              <Icon name="edit" size={16} aria-hidden />
            </button>
            <button
              type="button"
              onClick={clearProfile}
              className="booking-row-icon-button booking-row-icon-button-danger"
              aria-label="Clear profile"
            >
              <Icon name="close" size={16} aria-hidden />
            </button>
          </div>

          {/* Reason for visit — Flutter parity opens a popup with a
              search and clickable rows (appointment_profile_widget.dart
              showRFVPopup). The trigger is a styled "text field" that
              shows the resolved display label or a placeholder.
              Always rendered once a profile is selected so the user
              sees the field even before pre-book lands (Flutter
              "Enter reason for visit" with validation message). */}
          {true ? (
            <div className="booking-field" data-completion-target="reason">
              <span className="booking-field-label">Reason for visit</span>
              {/* Reason trigger: opens the inline picker below (Practice's), attached to it, full width. */}
              <button
                type="button"
                data-completion-focus
                onClick={() => {
                  setReasonOpen(!reasonOpen);
                  if (reasonOpen) setReasonSearch('');
                }}
                className="booking-select"
                data-open={reasonOpen || undefined}
              >
                <span className="truncate text-left flex-1">
                  {friendlyReasonDisplay(
                    selectedReason?.display,
                    selectedReason?.code,
                  ) ?? (
                    <span className="text-text-hint">Reason for visit</span>
                  )}
                </span>
                <Icon name="down" size={16} aria-hidden />
              </button>

              {reasonOpen && (
                <div className="booking-menu">
                  <div className="booking-menu-search">
                    <div className="booking-provider-search">
                      <Search className="booking-provider-search-icon" aria-hidden />
                      <input
                        autoFocus
                        placeholder="Search reasons"
                        aria-label="Search reasons"
                        value={reasonSearch}
                        onChange={(e) => setReasonSearch(e.target.value)}
                        className="booking-provider-search-input"
                      />
                    </div>
                  </div>
                  <div className="booking-reason-list scrollbar-hide">
                    {(() => {
                      const q = reasonSearch.trim().toLowerCase();
                      const visible = reasonOptions.filter((r) => {
                        if (!q) return true;
                        const label = friendlyReasonDisplay(r.display, r.code) ?? '';
                        return label.toLowerCase().includes(q);
                      });
                      if (visible.length === 0) {
                        return <p className="booking-hint-center">No matching reasons.</p>;
                      }
                      return (
                        <div className="booking-menu-list">
                          {visible.map((r) => {
                            const label = friendlyReasonDisplay(r.display, r.code) ?? 'Unnamed reason';
                            const isActive = selectedReason?.code === r.code;
                            return (
                              <button
                                key={r.code}
                                type="button"
                                onClick={() => {
                                  setValue(
                                    'reasonCode',
                                    {
                                      code: r.code,
                                      display: friendlyReasonDisplay(r.display, r.code) ?? '',
                                      definition: r.definition,
                                    },
                                    { shouldValidate: true },
                                  );
                                  setReasonOpen(false);
                                  setReasonSearch('');
                                }}
                                className="booking-menu-item"
                                data-on={isActive || undefined}
                                aria-pressed={isActive}
                              >
                                <span className="truncate">{label}</span>
                                {isActive ? <Icon name="check" size={16} aria-hidden /> : null}
                              </button>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              )}
            </div>
          ) : null}

          {/* Billing profile row — Flutter parity:
              - "+" opens the procedure-codes popup
              - The dropdown opens the billing-profiles popover */}
          <span className="booking-field-label booking-field-label-pad">Billing profile</span>
          <div className="booking-field booking-field-row">
            <AnchoredPopover open={procedureOpen} onOpenChange={setProcedureOpen}>
              <AnchoredPopoverTrigger
                render={
                  <button
                    type="button"
                    className="icon-action"
                    disabled={!billingProfileId}
                    aria-label="Add procedure codes"
                  >
                    <Plus className="size-4" />
                  </button>
                }
              />
              <AnchoredPopoverContent collisionAvoidance={{ fallbackAxisSide: 'end' }} align="start" className="w-80 p-0 gap-3">
                <ProcedureCodesPopupBody
                  onClose={() => setProcedureOpen(false)}
                />
              </AnchoredPopoverContent>
            </AnchoredPopover>

            <AnchoredPopover open={billingOpen} onOpenChange={(o) => { setBillingOpen(o); if (!o) setBillingSearch(''); }}>
              <AnchoredPopoverTrigger
                render={
                  <button
                    type="button"
                    className="booking-select booking-grow"
                  >
                    <span className="truncate">
                      {billingProfileName
                        ?? (preBook.isPending
                          ? 'Loading billing profile…'
                          : 'No billing profile')}
                    </span>
                    <Icon name="down" size={16} aria-hidden />
                  </button>
                }
              />
              <AnchoredPopoverContent collisionAvoidance={{ fallbackAxisSide: 'end' }} align="end" className="w-70 p-0 gap-3">
                <div className="px-3 py-2 border-b border-border-subtle">
                  <div className="relative">
                    <Search className="absolute left-3 top-3 size-4 text-text-hint pointer-events-none" />
                    <input
                      autoFocus
                      placeholder="Search"
                      value={billingSearch}
                      onChange={(e) => setBillingSearch(e.target.value)}
                      className="w-full h-10 pl-8 pr-3 rounded-lg bg-grey-100 text-sm text-text-primary placeholder:text-text-hint focus:outline-none focus:ring-1 focus:ring-brand-primary"
                    />
                  </div>
                </div>
                <div className="max-h-65 overflow-y-auto scrollbar-hide py-1">
                  {(() => {
                    const q = billingSearch.trim().toLowerCase();
                    const visible = (billingProfilesAll ?? []).filter((b) => {
                      if (!q) return true;
                      return (b.name ?? '').toLowerCase().includes(q);
                    });
                    if (visible.length === 0) {
                      return (
                        <p className="text-sm text-text-muted text-center py-6">
                          No matching billing profiles.
                        </p>
                      );
                    }
                    return visible.map((b, i) => {
                      const isSelected = !!b.id && b.id === billingProfileId;
                      const label = formatBillingProfile(b);
                      return (
                        <button
                          key={b.id || `bp-${i}`}
                          type="button"
                          onClick={() => {
                            if (!b.id) return;
                            setValue('billingProfileId', b.id);
                            setValue('billingProfileName', label);
                            setBillingOpen(false);
                            setBillingSearch('');
                          }}
                          className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left hover:bg-grey-100 text-sm text-text-primary"
                        >
                          <span className="truncate">{label}</span>
                          {isSelected ? <Check className="size-4 text-text-secondary flex-shrink-0" /> : null}
                        </button>
                      );
                    });
                  })()}
                </div>
              </AnchoredPopoverContent>
            </AnchoredPopover>
          </div>

          {/* F-SCH-137: free-of-charge follow-up banner. Verbatim Flutter copy
              (`appointment_profile_widget.dart:672`) + green-check glyph. Sits
              inside the profile card, after the billing row. */}
          {showFreeFollowUpBanner ? (
            <p className="booking-free">
              <Icon name="check" size={16} aria-hidden />
              Eligible for a free of charge follow-up.
            </p>
          ) : null}
        </div>

        {/* Edit-mode profile picker — Flutter `appointment_profile_widget.dart`
            edit-flow renders the alternative-profile grid below the
            selected card. Toggled by the pencil button above. */}
        {profileEditOpen && profiles.length > 0 && (
          <div className="mt-3 space-y-2">
            {location?.name ? (
              <p className="booking-sub-label">
                {location.name}
              </p>
            ) : null}
            <div className="grid grid-cols-3 gap-2">
              {profiles.map((profile, i) => {
                const Glyph = locationIcon(profile.location?.name ?? location?.name ?? '');
                const tint = parseColor(profile.color);
                const isCurrent = profile.id === selectedProfileId;
                return (
                  <button
                    key={profile.id || `edit-profile-${i}`}
                    type="button"
                    onClick={() => {
                      selectProfile(profile);
                      setProfileEditOpen(false);
                    }}
                    title={profile.profileName}
                    className={`flex items-center gap-2 p-2 rounded-lg border text-left transition-colors min-w-0 ${
                      isCurrent
                        ? 'border-brand-primary bg-blue-100'
                        : 'border-border-default bg-surface-card hover:border-brand-primary hover:bg-blue-100'
                    }`}
                  >
                    <span
                      aria-hidden
                      className={PROFILE_TINT_BADGE}
                      style={profileColorVars(tint)}
                    >
                      <Glyph className="size-4" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-text-primary truncate leading-tight">
                        {profile.profileName}
                      </p>
                      {profile.speciality?.display ? (
                        <p className="text-xs text-text-muted truncate leading-tight">
                          {profile.speciality.display}
                        </p>
                      ) : null}
                      <p className="text-xs text-text-hint leading-tight">
                        {profile.minutesDuration ?? 0}m
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}
        </>
      ) : isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-12 rounded-lg bg-grey-200 animate-pulse" />
          ))}
        </div>
      ) : profiles.length > 0 ? (
        <div className="space-y-2">
          {/* Sub-header — Flutter `appointment_profile_widget.dart:711` shows
              the appointment's currently-picked location name uppercase. */}
          {location?.name ? (
            <p className="booking-sub-label">
              {location.name}
            </p>
          ) : null}
          {/* 3-column grid — Flutter `appointment_profile_widget.dart:715-820`
              uses GridView.count(crossAxisCount: 3, childAspectRatio: 2.0). */}
          <div className="grid grid-cols-3 gap-2">
            {profiles.map((profile, i) => {
              const Glyph = locationIcon(profile.location?.name ?? location?.name ?? '');
              const tint = parseColor(profile.color);
              return (
                <button
                  key={profile.id || `profile-${i}`}
                  type="button"
                  onClick={() => selectProfile(profile)}
                  title={profile.profileName}
                  className="flex items-center gap-2 p-2 rounded-lg border border-border-default bg-surface-card hover:border-brand-primary hover:bg-blue-100 text-left transition-colors min-w-0"
                >
                  <span
                    aria-hidden
                    className={PROFILE_TINT_BADGE}
                    style={profileColorVars(tint)}
                  >
                    <Glyph className="size-4" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-text-primary truncate leading-tight">
                      {profile.profileName}
                    </p>
                    {profile.speciality?.display ? (
                      <p className="text-xs text-text-muted truncate leading-tight">
                        {profile.speciality.display}
                      </p>
                    ) : null}
                    <p className="text-xs text-text-hint leading-tight">
                      {profile.minutesDuration ?? 0}m
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      ) : (
        <p className="text-sm text-text-muted py-2">
          No profiles configured for this provider.
        </p>
      )}
    </div>
  );
}

/**
 * Some reason-for-visit rows arrive with `display` populated as the same
 * UUID as `code` (backend bug — a value-set entry without a localised
 * label). Hide the UUID rather than rendering it raw; if a sibling code
 * looks human-readable we use that instead. Flutter falls back to an
 * empty placeholder for these too (appointment_profile_widget.dart:384).
 */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
function friendlyReasonDisplay(
  display: string | null | undefined,
  code: string | null | undefined,
): string | null {
  const d = (display ?? '').trim();
  if (d && !UUID_RE.test(d)) return d;
  const c = (code ?? '').trim();
  if (c && !UUID_RE.test(c)) return c;
  return null;
}

/** Match the location chip icons used elsewhere in the panel. */
function locationIcon(name: string): ComponentType<SVGProps<SVGSVGElement>> {
  switch (name) {
    case 'Office':
      return Building2;
    case 'Home':
      return Home;
    case 'Virtual':
      return Video;
    default:
      return MapPin;
  }
}

/** Icon badge: the profile colour as the glyph and a 10% tint of it as the fill. */
const PROFILE_TINT_BADGE =
  'flex items-center justify-center h-8 w-8 rounded-md flex-shrink-0 text-[var(--pa-profile-color)] ' +
  'bg-[var(--pa-profile-tint)]';

/**
 * Profile color may arrive as Flutter ARGB int string ("4278231126"),
 * hex with or without #, or null. Default to muted grey if unparseable.
 */
function parseColor(raw: string | undefined | null): string {
  if (!raw) return PROFILE_COLOR_FALLBACK;
  if (raw.startsWith('#')) return raw;
  if (/^[0-9a-fA-F]{6,8}$/.test(raw)) return `#${raw.slice(-6)}`;
  const argb = Number(raw);
  if (Number.isFinite(argb)) {
    const hex = (argb & 0xffffff).toString(16).padStart(6, '0');
    return `#${hex}`;
  }
  return PROFILE_COLOR_FALLBACK;
}

/**
 * ProcedureCodesPopupBody — Flutter parity for the "Add Procedure Codes"
 * popup body (encounter_module/.../add_billing_procedure_popup.dart).
 *
 * Tabs: Provider Fees / All — both hit
 * `POST /api/Billing/Services/Fees` with the body's `type` flipped
 * between "provider" and "all". A search debounces to the same endpoint.
 *
 * Each row's "+" toggles the service in the form's `addedServices` list.
 * `serviceType` is "Default" for Provider Fees rows (Flutter parity in
 * `getType()` — `isProviderFeeSearch ? 'Default' : 'all'`) and "all" for
 * the catalog tab; this drives `IndividualService.type` on
 * PreBookCalculatePrice when that re-fire ships in a follow-up.
 */
function ProcedureCodesPopupBody({ onClose }: { onClose: () => void }) {
  const { watch, setValue } = useFormContext<AppointmentFormValues>();
  const providerId = watch('providerId');
  const location = watch('location');
  const patientId = watch('patientId');
  const billingProfileName = watch('billingProfileName');
  const addedServices = watch('addedServices');

  const [tab, setTab] = useState<'provider' | 'all'>('provider');
  const [search, setSearch] = useState('');
  const debounced = useDebounce(search, 350);

  const { data, isLoading, isFetching } = useServiceFees(
    providerId
      ? {
          providerId,
          locationId: location?.id ?? null,
          patientId: patientId ?? null,
          type: tab,
          query: debounced || undefined,
        }
      : null,
  );

  const results = data?.results ?? [];
  const selectedIds = new Set(addedServices.map((s) => s.id));

  const toggleService = (svc: ServiceFee) => {
    if (!svc.id) return;
    if (selectedIds.has(svc.id)) {
      setValue(
        'addedServices',
        addedServices.filter((s) => s.id !== svc.id),
      );
    } else {
      setValue('addedServices', [
        ...addedServices,
        {
          id: svc.id,
          serviceType: tab === 'provider' ? 'Default' : 'all',
          procedureCode: svc.procedureCode?.code,
          displayName: svc.procedureCode?.displayName,
          fee: svc.fees?.[0]?.fee,
        },
      ]);
    }
  };

  const formatFeeRow = (svc: ServiceFee): string => {
    const code = svc.procedureCode?.code ?? '';
    const fee = svc.fees?.[0]?.fee;
    const feeStr = typeof fee === 'number' ? `AED${fee}` : '';
    return [code, feeStr].filter(Boolean).join(' • ');
  };

  return (
    <div className="overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border-subtle">
        <p className="text-sm font-bold text-text-primary">
          Add Procedure Codes
        </p>
        <button
          type="button"
          onClick={onClose}
          className="icon-action"
          aria-label="Close"
        >
          <X className="size-4" />
        </button>
      </div>

      <div className="px-4 pt-3 pb-2 space-y-3">
        <div className="flex items-center gap-2 min-w-0">
          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-3 top-3 size-4 text-text-hint pointer-events-none" />
            <input
              placeholder="Search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full h-10 pl-8 pr-3 rounded-lg bg-grey-100 text-sm text-text-primary placeholder:text-text-hint focus:outline-none focus:ring-1 focus:ring-brand-primary"
            />
          </div>
          <button
            type="button"
            onClick={() => setTab('provider')}
            className={cn(
              'text-sm font-semibold flex-shrink-0',
              tab === 'provider' ? 'text-brand-primary' : 'text-text-muted',
            )}
          >
            Provider Fees
          </button>
          <button
            type="button"
            onClick={() => setTab('all')}
            className={cn(
              'text-sm font-semibold flex-shrink-0',
              tab === 'all' ? 'text-brand-primary' : 'text-text-muted',
            )}
          >
            All
          </button>
        </div>

        {billingProfileName ? (
          <div className="space-y-1 pb-2 border-b border-border-subtle">
            <p className="booking-sub-label">
              Selected Services
            </p>
            <p className="text-sm text-text-primary">{billingProfileName}</p>
            {addedServices.length > 0 ? (
              <ul className="pt-1 space-y-1">
                {addedServices.map((s) => (
                  <li
                    key={s.id}
                    className="flex items-center gap-2 text-xs text-text-muted"
                  >
                    <span className="truncate flex-1">
                      {s.displayName ?? s.procedureCode ?? s.id}
                    </span>
                    {typeof s.fee === 'number' ? (
                      <span className="text-text-hint">AED{s.fee}</span>
                    ) : null}
                    <button
                      type="button"
                      onClick={() =>
                        setValue(
                          'addedServices',
                          addedServices.filter((x) => x.id !== s.id),
                        )
                      }
                      className="icon-action"
                      aria-label="Remove service"
                    >
                      <X className="size-3" />
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}
      </div>

      <div
        className={cn(
          'max-h-65 overflow-y-auto overflow-x-hidden scrollbar-hide px-4 pb-3',
          // Hide scrollbar (Webkit + Firefox) — Flutter parity. List
          // remains scrollable with wheel / touch / keyboard.
        )}
      >
        {isLoading ? (
          <div className="space-y-2 py-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-12 rounded bg-grey-200 animate-pulse" />
            ))}
          </div>
        ) : results.length === 0 ? (
          <p className="text-sm text-text-muted text-center py-6">
            {isFetching ? 'Searching…' : 'No matching services.'}
          </p>
        ) : (
          <ul className="divide-y divide-border-subtle">
            {results.map((svc, i) => {
              const isAdded = !!svc.id && selectedIds.has(svc.id);
              return (
                <li
                  key={svc.id || `svc-${i}`}
                  className="flex items-start gap-2 py-3 min-w-0"
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-text-primary truncate">
                      {svc.procedureCode?.displayName ?? '—'}
                    </p>
                    {svc.shortDescription ? (
                      <p className="text-xs text-text-muted leading-snug break-words">
                        {svc.shortDescription}
                      </p>
                    ) : null}
                    <p className="text-xs text-text-muted truncate">
                      {formatFeeRow(svc)}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => toggleService(svc)}
                    className={cn(
                      'p-1 rounded-md flex-shrink-0',
                      isAdded
                        ? 'text-status-error hover:bg-status-error-bg'
                        : 'text-brand-primary hover:bg-blue-100',
                    )}
                    aria-label={isAdded ? 'Remove service' : 'Add service'}
                  >
                    {isAdded ? <X className="size-4" /> : <Plus className="size-4" />}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

/** The design's location words for the profile line (SchedBookingForm `LOCATION_LABEL`). */
const PROFILE_LOCATION_LABEL: Record<string, string> = { Office: 'In clinic', Virtual: 'Video', Home: 'Home' };
